import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReportsService,
  type ReportDashboardWidgetInstance,
  type ReportDashboardConfig,
  type ReportDashboardView,
  type ReportFunnelStatusFunnelResponse,
  type ReportFunnelStatusTimeseriesResponse,
  type ReportPropertyInterestRankingResponse,
  type ReportPropertyTypeDistributionResponse,
  type ReportUserOption,
  type ReportUserAssignmentRankingResponse,
  type ReportWidgetCatalogItem,
} from 'src/api/generated';
import {
  isFunnelStatusFunnelWidget,
  isFunnelStatusTimeseriesWidget,
  isPropertyInterestRankingWidget,
  isPropertyTypeDistributionWidget,
  isUserAssignmentRankingWidget,
} from '../utils/report-dashboard';

const DASHBOARD_QUERY_KEY = ['reports', 'dashboard'];
const CATALOG_QUERY_KEY = ['reports', 'widget-catalog'];
const USER_OPTIONS_QUERY_KEY = ['reports', 'widget-users'];

/**
 * Union of the possible widget query payloads returned by the reports API.
 */
export type ReportWidgetQueryData =
  | ReportFunnelStatusFunnelResponse
  | ReportFunnelStatusTimeseriesResponse
  | ReportPropertyTypeDistributionResponse
  | ReportPropertyInterestRankingResponse
  | ReportUserAssignmentRankingResponse;

/**
 * Loads the resolved dashboard view for the current user.
 */
export const useReportsDashboard = () => {
  const query = useQuery<ReportDashboardView>({
    queryKey: DASHBOARD_QUERY_KEY,
    queryFn: async () => ReportsService.getReportDashboard(),
  });

  return {
    ...query,
    dashboard: query.data,
    loading: query.isLoading,
  };
};

/**
 * Loads the widget catalog available to the current user.
 */
export const useReportWidgetCatalog = () => {
  const query = useQuery<ReportWidgetCatalogItem[]>({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: async () => ReportsService.getReportWidgetsCatalog(),
  });

  return {
    ...query,
    catalog: query.data ?? [],
    loading: query.isLoading,
  };
};

/**
 * Loads the available report user filter options.
 */
export const useReportWidgetUsers = (enabled: boolean) => {
  const query = useQuery<ReportUserOption[]>({
    queryKey: USER_OPTIONS_QUERY_KEY,
    queryFn: async () => ReportsService.getReportWidgetUsers(),
    enabled,
  });

  return {
    ...query,
    users: query.data ?? [],
    loading: query.isLoading,
  };
};

/**
 * Persists a dashboard config and refreshes dependent queries.
 */
export const useUpdateReportsDashboard = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (config: ReportDashboardConfig) =>
      ReportsService.updateReportDashboard(config),
    onMutate: async (config) => {
      await queryClient.cancelQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });

      const previousDashboard =
        queryClient.getQueryData<ReportDashboardView>(DASHBOARD_QUERY_KEY);

      if (previousDashboard) {
        queryClient.setQueryData<ReportDashboardView>(DASHBOARD_QUERY_KEY, {
          ...previousDashboard,
          config,
          isUsingDefault: false,
        });
      }

      return {
        previousDashboard,
      };
    },
    onError: (_error, _config, context) => {
      if (context?.previousDashboard) {
        queryClient.setQueryData(
          DASHBOARD_QUERY_KEY,
          context.previousDashboard
        );
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(DASHBOARD_QUERY_KEY, data);
      void queryClient.invalidateQueries({
        queryKey: CATALOG_QUERY_KEY,
      });
    },
  });

  return {
    ...mutation,
    loading: mutation.isPending,
  };
};

/**
 * Resets the dashboard to the built-in default.
 */
export const useResetReportsDashboard = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => ReportsService.deleteReportDashboard(),
    onSuccess: (data) => {
      queryClient.setQueryData(DASHBOARD_QUERY_KEY, data);
    },
  });

  return {
    ...mutation,
    loading: mutation.isPending,
  };
};

/**
 * Queries one widget using the backend reports API.
 */
export const useReportWidgetQuery = (
  widget: ReportDashboardWidgetInstance,
  enabled: boolean
) => {
  const query = useQuery<ReportWidgetQueryData>({
    queryKey: ['reports', 'widget-query', widget.kind, widget.filters],
    queryFn: async () => {
      if (isFunnelStatusFunnelWidget(widget)) {
        return ReportsService.queryFunnelStatusFunnelWidget(widget.filters);
      }

      if (isFunnelStatusTimeseriesWidget(widget)) {
        return ReportsService.queryFunnelStatusTimeseriesWidget(widget.filters);
      }

      if (isPropertyInterestRankingWidget(widget)) {
        return ReportsService.queryPropertyInterestRankingWidget(
          widget.filters
        );
      }

      if (isUserAssignmentRankingWidget(widget)) {
        return ReportsService.queryUserAssignmentRankingWidget(widget.filters);
      }

      if (isPropertyTypeDistributionWidget(widget)) {
        return ReportsService.queryPropertyTypeDistributionWidget(
          widget.filters
        );
      }

      throw new Error('Unsupported widget kind');
    },
    enabled,
  });

  return {
    ...query,
    loading: query.isLoading,
  };
};
